import { api } from './api-base';
import { User, TopicVisibility, BasemapId } from '../types';

// Users API (profiles)
export const usersApi = {
  // Basic profile with recent topics and comments
  getProfile: async (id: string): Promise<{
    id: string;
    email: string;
    username?: string;
    reputation: number;
    role: User['role'];
    createdAt: string;
    showTopicsInProfile: boolean;
    showCommentsInProfile: boolean;
    allowSearchIndexing?: boolean;
    topics: Array<{ id: string; title: string; createdAt: string; commentCount: number; upvotes: number; downvotes: number; visibility?: TopicVisibility; basemap?: BasemapId; showInProfile: boolean; }>;
    comments: Array<{ id: string; topicId: string; content: string; createdAt: string; upvotes: number; downvotes: number; showInProfile: boolean; topic?: { visibility?: TopicVisibility } | null; }>;
    moderationActions?: Array<{
      id: string;
      contentType: 'TOPIC' | 'COMMENT';
      contentId: string;
      actionType: 'DELETE' | 'RESTORE' | 'WARNING';
      reason: string;
      moderatorId: string;
      createdAt: string;
      moderator: {
        id: string;
        email: string;
        username: string;
        reputation: number;
        role: 'READER' | 'AUTHOR' | 'MODERATOR';
        createdAt: string;
      };
      userId: string;
      isVisible: boolean;
    }>;
    followerCount?: number;
    followingCount?: number;
    isFollowing?: boolean;
    isFollowedBy?: boolean;
  }> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  updateProfileVisibility: async (id: string, payload: {
    showTopicsInProfile?: boolean;
    showCommentsInProfile?: boolean;
    applyToTopics?: boolean;
    applyToComments?: boolean;
    allowSearchIndexing?: boolean;
  }) => {
    const response = await api.patch(`/users/${id}/profile-visibility`, payload);
    return response.data as { showTopicsInProfile: boolean; showCommentsInProfile: boolean; allowSearchIndexing: boolean };
  },

  updateTopicProfileVisibility: async (userId: string, topicId: string, showInProfile: boolean) => {
    const response = await api.patch(`/users/${userId}/topics/${topicId}/profile-visibility`, { showInProfile });
    return response.data as { id: string; showInProfile: boolean };
  },

  updateCommentProfileVisibility: async (userId: string, commentId: string, showInProfile: boolean) => {
    const response = await api.patch(`/users/${userId}/comments/${commentId}/profile-visibility`, { showInProfile });
    return response.data as { id: string; showInProfile: boolean };
  },
  
  // Moderator activities (actions performed by this moderator)
  getModeratorActivities: async (id: string): Promise<{
    moderationActivities: Array<{
      id: string;
      contentType: 'TOPIC' | 'COMMENT';
      contentId: string;
      actionType: 'DELETE' | 'RESTORE' | 'WARNING';
      reason: string;
      moderatorId: string;
      createdAt: string;
      targetUser: {
        id: string;
        email: string;
        username: string;
      };
      isVisible: boolean;
    }>;
  }> => {
    const response = await api.get(`/users/${id}/moderator-activities`);
    return response.data;
  },

  followUser: async (id: string): Promise<{ followerCount: number; isFollowing: boolean }> => {
    const response = await api.post(`/users/${id}/follow`);
    return response.data;
  },

  unfollowUser: async (id: string): Promise<{ followerCount: number; isFollowing: boolean }> => {
    const response = await api.delete(`/users/${id}/follow`);
    return response.data;
  },

  getFollowers: async (id: string): Promise<{ followers: Array<{ id: string; username?: string; email: string; createdAt: string }> }> => {
    const response = await api.get(`/users/${id}/followers`);
    return response.data;
  }
};
